<script setup>
import { computed } from 'vue'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import { getSignteqSignatureUrl } from '../../../utilities/api'
import { onMounted } from 'vue'
import { onUnmounted } from 'vue'
import { ref } from 'vue'
import { storeSignteqSignaturedDocument } from '../../../utilities/api'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import InvestmentProcessNavigationButtons from '../InvestmentProcessNavigationButtons.vue'
import LoadingSpinner from '../../base/LoadingSpinner.vue'
import { getExternalKycProcess } from '../../../utilities/api'
import { isNaturalProfile } from '../../../utilities/investment-process/useUserProfileType'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import { resetExternalKycProcess } from '../../../utilities/api'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { getAccountLink } from '../../../utilities/useLinks'
import useEventStore from '../../../utilities/useEvent'
import { getPlatformName } from '../../../utilities/usePlatformSettings'

const eventStore = useEventStore()

const iFrameHeight = ref('600px')
const isLoading = ref(true)
const isSuccess = ref(false)
const order_uid = computed(() => useInvestmentProcessDataState().createdOrder.order_uid)
const showIdnowWarning = computed(
    () => externalKycProcess.value?.type !== 'signteq' || externalKycProcess.value?.status !== 'done'
)
const verificationLink = ref(false)
const externalKycProcess = ref(null)
const numberOfDocuments = ref(0)

async function fetchSignteqSignatureUrl() {
    try {
        const response = await getSignteqSignatureUrl(order_uid.value)
        if (response.status === 200) {
            verificationLink.value = response.data.redirect_url
            numberOfDocuments.value = response.data.number_of_documents
            eventStore.trackEvent({
                event: 'signature_process',
                data: {
                    provider: 'signteq',
                    status: 'started',
                },
                platform_name: getPlatformName(),
            })
        }
    } catch (e) {
        console.log(e)
    }
    isLoading.value = false
}

async function fetchExternalKycProcess() {
    try {
        const response = await getExternalKycProcess()
        if (response.status === 200) {
            externalKycProcess.value = response.data
        }
    } catch (e) {
        console.log(e)
    }
}

async function checkMessage(message) {
    if (message.data.type === 'COMPLETED' && message.data.data.document) {
        await storeSignteqSignaturedDocument(message.data.data.document, order_uid.value).then(async () => {
            numberOfDocuments.value--
            if (numberOfDocuments.value === 0) {
                isSuccess.value = true
                await eventStore.trackEvent({
                    event: 'signature_process',
                    data: {
                        provider: 'signteq',
                        status: 'completed',
                    },
                    platform_name: getPlatformName(),
                })
            }
        })
    }
}

async function getSignteqSignatureUrlOrPopUpWarning() {
    await fetchExternalKycProcess().then(() => {
        if (externalKycProcess.value?.type === 'signteq') {
            fetchSignteqSignatureUrl()
        } else {
            isLoading.value = false
        }
    })
}

async function handleResetExternalKycProcess() {
    try {
        await resetExternalKycProcess(
            isNaturalProfile() ? usePersonalAccountDataState().accountId : useLegalAccountDataState().accountId
        )
    } catch (e) {
        return console.log(e)
    }
    window.location.replace(getAccountLink() + '?menu=Verification')
}

onMounted(() => {
    getSignteqSignatureUrlOrPopUpWarning()
    window.addEventListener('message', checkMessage)
})

onUnmounted(() => {
    window.removeEventListener('message', checkMessage)
})
</script>
<template>
    <div class="px-4 py-6 sm:p-6 lg:p-8">
        <div>
            <h3 class="pb-3 text-base font-bold text-gray-700">Signteq {{ $t('investment_process.signature') }}</h3>
            <p
                v-if="getCampaignData().asset_data.signature_legal_notice"
                class="mb-3 border border-gray-200 p-2 font-medium text-gray-500"
                v-html="getCampaignData().asset_data.signature_legal_notice"></p>

            <div
                v-if="isLoading"
                class="align-items-center flex justify-center border border-gray-200"
                :style="`height: ${iFrameHeight}`">
                <LoadingSpinner class="h-10 w-10 fill-primary" />
            </div>

            <div v-if="!isLoading && showIdnowWarning" class="border border-gray-200 p-10">
                <h5 class="mb-4">{{ $t('investment_process.modal_reset_verification_header') }}</h5>
                <p v-html="$t('investment_process.modal_reset_verification_body')" />

                <div class="mt-5 flex justify-center">
                    <BaseButtonPrimary class="font-semibold" :sticky="false" @click="handleResetExternalKycProcess()"
                        >{{ $t('investment_process.modal_reset_verification_button') }}
                    </BaseButtonPrimary>
                </div>
            </div>

            <iframe
                v-if="verificationLink"
                class="w-full border border-gray-200 pt-8"
                :style="`height: ${iFrameHeight};`"
                :src="verificationLink"
                title="Signteq signature" />
        </div>
        <InvestmentProcessNavigationButtons
            :next-button-disabled="!isSuccess"
            :next-button-text="$t('investment_process.confirm_signature')" />
    </div>
</template>
